import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useApi } from '@shared/services/api';

type SearchMode = 'domain' | 'keyword' | 'linkedin';

/** A Coresignal company as a search lists it. */
interface CoresignalCompanyPreview {
  id: number;
  industry: string | null;
  locationHqCountry: string | null;
  name: string | null;
  sizeRange: string | null;
  websitesLinkedin: string | null;
  websitesMain: string | null;
}

/** What collecting a company adds to its preview. */
interface CoresignalCompanyDetails extends CoresignalCompanyPreview {
  description: string | null;
  employeeCount: number | null;
  followers: number | null;
  ticker: string | null;
  type: string | null;
  uniqueDomain: boolean | null;
}

interface CoresignalCompanySearchProps {
  companyId: number;
  /** The company's own name, the default keyword. */
  companyName?: string;
  /** The company's own URL, the default domain. */
  companyUrl?: string;
  /** The Coresignal id currently stored, to mark it in results. */
  currentId: string | null;
  isSetting: boolean;
  onSelect: (coresignalId: number) => void;
}

const PAGE_SIZE = 20;

const MODE_LABELS: Record<SearchMode, { label: string; placeholder: string }> = {
  domain: { label: 'Domain', placeholder: 'acme.com' },
  keyword: { label: 'Keyword', placeholder: 'Acme Corporation' },
  linkedin: { label: 'LinkedIn URL', placeholder: 'https://www.linkedin.com/company/acme' },
};

/**
 * The bare host of a stored company URL, for the default domain search.
 * @param url - A URL or bare domain.
 */
function hostOf(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * Find a Coresignal company by domain, LinkedIn URL or keyword, and choose one
 * as this company's id.
 *
 * A search returns Coresignal's preview rows — name, website, LinkedIn, size,
 * industry, country — which are usually enough to pick. "Details" collects a
 * candidate for its description, headcount, followers and whether Coresignal
 * marks it as owning its domain, which is what separates a parent company from
 * the branches and product pages filed under the same domain. Each collect
 * costs a credit, so nothing is collected until asked.
 */
const CoresignalCompanySearch: React.FC<CoresignalCompanySearchProps> = ({
  companyId,
  companyName,
  companyUrl,
  currentId,
  isSetting,
  onSelect,
}) => {
  const api = useApi();
  const [mode, setMode] = useState<SearchMode>('domain');
  const [queries, setQueries] = useState<Record<SearchMode, string>>({
    domain: hostOf(companyUrl),
    keyword: companyName ?? '',
    linkedin: '',
  });
  const [results, setResults] = useState<(CoresignalCompanyPreview | CoresignalCompanyDetails)[] | null>(null);
  const [details, setDetails] = useState<Record<number, CoresignalCompanyDetails>>({});
  const [collectingId, setCollectingId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [searchedMode, setSearchedMode] = useState<SearchMode>('domain');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (pageToLoad: number): Promise<void> => {
    const query = queries[mode].trim();
    if (!query) return;
    setIsSearching(true);
    setError(null);
    setSelectedId(null);
    const response = await api.post(`/admin/company/${companyId}/coresignal-search`, {
      mode,
      page: pageToLoad,
      query,
    });
    setIsSearching(false);
    if (response?.error) {
      setResults(null);
      setError(response.message ?? 'Coresignal search failed');
      return;
    }
    const rows: (CoresignalCompanyPreview | CoresignalCompanyDetails)[] = response?.results ?? [];
    setResults(rows);
    setTotal(typeof response?.total === 'number' ? response.total : null);
    setPage(pageToLoad);
    setSearchedMode(mode);
    // A LinkedIn search collects its one result, so its details are already in hand.
    if (mode === 'linkedin') {
      setDetails((previous) => ({
        ...previous,
        ...Object.fromEntries(rows.map((row) => [row.id, row as CoresignalCompanyDetails])),
      }));
    }
  };

  const collect = async (coresignalId: number): Promise<void> => {
    setCollectingId(coresignalId);
    setError(null);
    const response = await api.post(`/admin/company/${companyId}/coresignal-collect`, { coresignalId });
    setCollectingId(null);
    if (response?.error) {
      setError(response.message ?? `Could not collect ${coresignalId}`);
      return;
    }
    setDetails((previous) => ({ ...previous, [coresignalId]: response as CoresignalCompanyDetails }));
  };

  const renderResult = (row: CoresignalCompanyPreview): React.ReactNode => {
    const detail = details[row.id];
    const isSelected = selectedId === row.id;
    const isCurrent = currentId === String(row.id);

    return (
      <Card
        key={row.id}
        variant="outlined"
        sx={{
          backgroundColor: isCurrent ? 'action.selected' : undefined,
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
        }}
      >
        <CardActionArea onClick={() => setSelectedId(row.id)} aria-label={`Select ${row.name ?? row.id}`}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ alignItems: 'flex-start', display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {row.name || 'Unknown'}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  (ID: {row.id})
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {detail?.uniqueDomain && <Chip label="Owns domain" size="small" color="info" />}
                {isCurrent && <Chip label="Current" size="small" color="success" />}
                {isSelected && <Chip label="Selected" size="small" color="primary" />}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
              {row.websitesMain && (
                <Typography variant="body2" color="text.secondary">
                  {row.websitesMain}
                </Typography>
              )}
              {row.websitesLinkedin && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {row.websitesLinkedin}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {row.locationHqCountry && <Typography variant="body2">{row.locationHqCountry}</Typography>}
              {row.industry && <Typography variant="body2">{row.industry}</Typography>}
              {row.sizeRange && <Typography variant="body2">{row.sizeRange}</Typography>}
            </Box>

            {detail && (
              <Box sx={{ borderColor: 'divider', borderTop: 1, display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1, pt: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {detail.type && <Typography variant="body2">{detail.type}</Typography>}
                  {detail.ticker && <Typography variant="body2">Ticker: {detail.ticker}</Typography>}
                  {detail.employeeCount != null && (
                    <Typography variant="body2">{detail.employeeCount.toLocaleString()} employees</Typography>
                  )}
                  {detail.followers != null && (
                    <Typography variant="body2">{detail.followers.toLocaleString()} followers</Typography>
                  )}
                </Box>
                {detail.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detail.description}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </CardActionArea>
        {!detail && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1, px: 1.5 }}>
            <Button size="small" onClick={() => collect(row.id)} disabled={collectingId != null}>
              {collectingId === row.id ? <CircularProgress size={16} /> : 'Details'}
            </Button>
          </Box>
        )}
      </Card>
    );
  };

  const pageCount = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_event, value: null | SearchMode) => value && setMode(value)}
          aria-label="Search by"
        >
          {(Object.keys(MODE_LABELS) as SearchMode[]).map((key) => (
            <ToggleButton key={key} value={key}>
              {MODE_LABELS[key].label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          label={`Search Coresignal by ${MODE_LABELS[mode].label.toLowerCase()}`}
          placeholder={MODE_LABELS[mode].placeholder}
          value={queries[mode]}
          onChange={(event) => setQueries((previous) => ({ ...previous, [mode]: event.target.value }))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') search(1);
          }}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Button variant="outlined" onClick={() => search(1)} disabled={isSearching || !queries[mode].trim()}>
          {isSearching ? <CircularProgress size={20} /> : 'Search Coresignal'}
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {results != null && results.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No Coresignal companies found.
        </Typography>
      )}

      {results != null && results.length > 0 && (
        <>
          <Typography variant="subtitle2">
            {total != null ? `${total.toLocaleString()} results` : `${results.length} results`}
            {searchedMode !== 'linkedin' && pageCount != null && pageCount > 1 ? ` — page ${page} of ${pageCount}` : ''}
          </Typography>

          {results.map((row) => renderResult(row))}

          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {searchedMode !== 'linkedin' && (
                <>
                  <Button size="small" onClick={() => search(page - 1)} disabled={isSearching || page <= 1}>
                    Previous
                  </Button>
                  <Button
                    size="small"
                    onClick={() => search(page + 1)}
                    disabled={isSearching || (pageCount != null ? page >= pageCount : results.length < PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </>
              )}
            </Box>
            <Button
              variant="contained"
              onClick={() => selectedId != null && onSelect(selectedId)}
              disabled={selectedId == null || isSetting}
            >
              {isSetting ? <CircularProgress size={20} /> : 'Set Selected ID'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CoresignalCompanySearch;
